import type {ContentRelationshipField, KeyTextField, LinkField, RichTextField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import Link from 'next/link'
import {useLinkResolver} from 'prismicio'
import type {ButtonDocument, HeadlineSectionSliceDefaultItem} from 'prismicio-types'
import {RatingsSection} from 'slices/HeroV2/RatingsSection'
import {ButtonV2} from 'src/common/ButtonV2'
import {Icon} from 'src/common/Icon'
import {EVENTS, getButtonsData, track} from 'src/common/helpers'
import {sanitizeHtml} from 'src/common/helpers/sanitizeHtml'
import type {BackgroundColors} from 'src/common/types'

type HeadlineProps = {
  sliceType?: string
  variation?: string
  backgroundColor?: BackgroundColors
  isCentered?: boolean
  eyebrow?: KeyTextField
  title?: KeyTextField
  subtext?: KeyTextField
  isSubtextFullWidth?: boolean
  primaryButtonLabel?: KeyTextField
  primaryButtonLink?: LinkField
  secondaryButtonLabel?: KeyTextField
  secondaryButtonLink?: LinkField
  secondaryButtonMktgId?: KeyTextField
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4'
  primaryButtonMktgId?: KeyTextField
  pageUid?: string
  componentIndex?: number
  parentComponent?: string
  subtextRte?: RichTextField
  ratings?: HeadlineSectionSliceDefaultItem[]
  ratingsOnTop?: boolean
  primaryButtonAnchor?: KeyTextField
  ratingsType?: 'Icons' | 'Stars' | 'Animated'
  primaryButton?: ContentRelationshipField
  secondaryButton?: ContentRelationshipField
  buttonsData?: ButtonDocument[]
  enableReskin?: boolean
}

export const Headline = ({
  isCentered,
  eyebrow,
  title,
  subtext,
  isSubtextFullWidth,
  primaryButtonLabel,
  primaryButtonLink,
  secondaryButtonLabel,
  secondaryButtonLink,
  secondaryButtonMktgId,
  headingLevel = 'h1',
  primaryButtonMktgId,
  pageUid,
  componentIndex,
  parentComponent,
  subtextRte,
  ratings,
  ratingsOnTop = false,
  primaryButtonAnchor = '',
  ratingsType = 'Stars',
  primaryButton,
  secondaryButton,
  buttonsData,
  enableReskin = false,
}: HeadlineProps) => {
  const {linkResolver} = useLinkResolver()
  const sanitizedTitle = sanitizeHtml(title!)
  const {primaryButtonData, secondaryButtonData} = getButtonsData({buttonsData, primaryButton, secondaryButton})
  const primaryLink = primaryButtonData?.link || primaryButtonLink

  const href = isFilled.link(primaryLink)
    ? linkResolver(primaryLink as LinkField, 'primary-button-headline')
    : `#${primaryButtonAnchor}`

  const handleLinkClick = () => {
    track({
      event: EVENTS.CLICK,
      pageName: pageUid,
      location: `component-${componentIndex}`,
      dataType: 'link',
      mktgId: 'subtext-link',
    })
  }

  return (
    <div className="grid-base gap-y-8 px-5 lg:px-0">
      <div
        className={cn('col-span-full grid grid-cols-[subgrid] gap-y-3', {
          'lg:col-span-6': !isCentered,
          'md:col-start-3 md:col-end-11 text-center': isCentered,
        })}
      >
        {eyebrow && (
          <p
            className={cn('text-body-xs col-span-4 uppercase text-accent-purple', {
              'md:col-start-3 md:col-end-7': isCentered,
            })}
          >
            {eyebrow}
          </p>
        )}
        <h1
          className={cn('col-span-full text-moss-black', {
            'text-h2 md:text-h1': headingLevel === 'h1',
            'text-h3 md:text-h2': headingLevel === 'h2',
            'text-h3': headingLevel === 'h3',
            'text-h5 md:text-h4': headingLevel === 'h4',
          })}
          dangerouslySetInnerHTML={{__html: sanitizedTitle}}
        />
        {isFilled.keyText(subtext) && !isFilled.richText(subtextRte) && (
          <p
            className={cn('text-body col-span-4 text-grey-550', {
              'md:col-start-2 md:col-end-8': isCentered && !isSubtextFullWidth,
              'col-span-full': isSubtextFullWidth,
            })}
          >
            {subtext}
          </p>
        )}
        {isFilled.richText(subtextRte) && (
          <PrismicRichText
            field={subtextRte}
            components={{
              paragraph: ({children}) => (
                <p
                  className={cn('col-span-4 text-grey-550', {
                    'md:col-start-2 md:col-end-8': isCentered && !isSubtextFullWidth,
                    'col-span-full': isSubtextFullWidth,
                    'text-tg-body-l': enableReskin,
                    'text-body': !enableReskin,
                  })}
                >
                  {children}
                </p>
              ),
              hyperlink: ({children, node}) => (
                <Link
                  className={cn('text-body underline underline-offset-[3px]', {
                    'text-body': !enableReskin,
                    'text-tg-body-l': enableReskin,
                  })}
                  href={linkResolver(node.data)}
                  {...(node.data.link_type === 'Web' && node.data.target && {target: node.data.target})}
                  onClick={handleLinkClick}
                >
                  {children}
                </Link>
              ),
              list: ({children}) => (
                <ul
                  className={cn('text-body flex list-inside list-none justify-center gap-4 pt-5 text-moss-black', {
                    'col-span-full md:col-start-2 md:col-end-8': isCentered && !isSubtextFullWidth,
                    'col-span-full': isSubtextFullWidth,
                  })}
                >
                  {children}
                </ul>
              ),
              listItem: ({children}) => (
                <li className="text-body-bold-s flex gap-2">
                  <Icon name="filled-check-v2" className="size-5 text-moss-green-450-a" />
                  {children}
                </li>
              ),
            }}
          />
        )}
      </div>
      {(primaryButtonLabel || primaryButtonData) && (
        <div
          className={cn('col-span-full row-start-2 flex gap-2', {
            'justify-center': isCentered,
            'justify-start': !isCentered,
            'row-start-2': !ratingsOnTop,
            'row-start-3': ratingsOnTop,
          })}
        >
          <ButtonV2
            variant="primary"
            href={href}
            trackingData={{
              mktgId: primaryButtonData?.marketing_id || (primaryButtonMktgId as string),
              location: `component-${componentIndex}-${parentComponent}`,
              dataType: primaryButtonData?.intent || 'primary-button',
              pageName: pageUid,
              label: primaryButtonData?.label || primaryButtonLabel || '',
            }}
            id={`component-${componentIndex}-${parentComponent}-1`}
          >
            {primaryButtonData?.label || primaryButtonLabel}
          </ButtonV2>
          {(secondaryButtonData || (secondaryButtonLabel && secondaryButtonLink)) && (
            <ButtonV2
              variant="secondary"
              href={linkResolver(
                secondaryButtonData?.link || (secondaryButtonLink as LinkField),
                'secondary-button-headline',
              )}
              trackingData={{
                mktgId: secondaryButtonData?.marketing_id || (secondaryButtonMktgId as string),
                location: `component-${componentIndex}-${parentComponent}`,
                dataType: secondaryButtonData?.intent || 'secondary-button',
                pageName: pageUid,
                label: secondaryButtonData?.label || secondaryButtonLabel || '',
              }}
              id={`component-${componentIndex}-${parentComponent}-2`}
            >
              {secondaryButtonData?.label || secondaryButtonLabel}
            </ButtonV2>
          )}
        </div>
      )}
      {!!ratings?.length && (
        <div
          className={cn('col-span-full flex gap-2', {
            'justify-center': isCentered,
            'justify-start': !isCentered,
            'row-start-1': ratingsOnTop,
          })}
        >
          <RatingsSection
            ratings={ratings}
            type={ratingsType}
            iconContainerColor={enableReskin ? 'Moss Black' : undefined}
          />
        </div>
      )}
    </div>
  )
}
